/**
 * Catalog browse nav — mirrors the Oxbo-M admin left rail (§22 wireframe): a
 * searchable, grouped list of catalog keys. Selecting one loads its values into
 * the detail pane. Read-only: this is navigation, not editing.
 */
import { useMemo, useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { CATALOG_REGISTER } from "./register";

export interface CatalogAdminNavProps {
  activeKey: string;
  onSelect: (key: string) => void;
}

export function CatalogAdminNav({ activeKey, onSelect }: CatalogAdminNavProps) {
  const { t } = useLocale();
  const [query, setQuery] = useState("");

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return CATALOG_REGISTER;
    return CATALOG_REGISTER.map((g) => ({
      ...g,
      entries: g.entries.filter(
        (e) => e.label.toLowerCase().includes(q) || e.key.toLowerCase().includes(q),
      ),
    })).filter((g) => g.entries.length > 0);
  }, [query]);

  return (
    <nav className="catadmin-nav" aria-label={t("catadmin.nav.aria")}>
      <div className="catadmin-brand">
        <span className="catadmin-brand-mark" aria-hidden="true">
          M
        </span>
        <span className="catadmin-brand-text">
          <b>Oxbo-M</b>
          <small>{t("catadmin.nav.chrome")}</small>
        </span>
      </div>

      <label className="catadmin-search">
        <span aria-hidden="true">⌕</span>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("catadmin.nav.find")}
          aria-label={t("catadmin.nav.find")}
        />
      </label>

      {groups.length === 0 ? (
        <p className="catadmin-nav-empty">{t("catadmin.nav.noMatch")}</p>
      ) : (
        groups.map((g) => (
          <div key={g.titleKey} className="catadmin-nav-group">
            <h5>{t(g.titleKey)}</h5>
            {g.entries.map((e) => (
              <button
                key={e.key}
                type="button"
                className={`catadmin-nav-link${e.key === activeKey ? " active" : ""}`}
                aria-current={e.key === activeKey ? "true" : undefined}
                onClick={() => onSelect(e.key)}
              >
                <span className="catadmin-nav-label">{e.label}</span>
                <span className="catadmin-nav-key">{e.key}</span>
              </button>
            ))}
          </div>
        ))
      )}
    </nav>
  );
}
